import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { GoogleOAuthProvider } from "@react-oauth/google";
import Header from "./components/Header";
import Home from "./pages/Home";
import WordExtractor from "./pages/WordExtractor";
import SentenceExtractor from "./components/SentenceExtractor";
import WordHistory from "./pages/WordHistory";
import SentenceHistory from "./pages/SentenceHistory";
import Dashboard from "./pages/Dashboard";
import Settings from "./pages/Settings";
import QuizPage from "./pages/QuizPage";
import AIStudyPage from "./pages/AIStudyPage";
import FeedPage from './pages/FeedPage';
import MyStoryPage from './pages/MyStoryPage';
import CommunityPage from './pages/CommunityPage';
import AdminPage from "./pages/AdminPage";
import BatchResultsPage from "./pages/BatchResultsPage";
import FeedSettingsPage from './pages/FeedSettingsPage';
import LoginPage from "./LoginPage";
import LandingPage from "./pages/LandingPage";
import GuestGuidePage from "./pages/GuestGuidePage";
import DeepLTestPage from "./pages/DeepLTestPage";
import LabsPage from "./labs/LabsPage";
import StockPage from "./labs/stock/StockPage";
import TechnicalPage from "./labs/stock/TechnicalPage";
import BacktestPage from "./labs/backtest/BacktestPage"; // Added Backtest
import ScreenerPage from "./labs/backtest/ScreenerPage"; // Added Screener
import ScreenerHistoryPage from "./labs/backtest/ScreenerHistoryPage"; // Added Screener History
import MoneyFlowPage from "./labs/moneyflow/MoneyFlowPage"; // Added Money Flow
import MoneyFlowHistoryPage from "./labs/moneyflow/MoneyFlowHistoryPage"; // Added Money Flow History
import LivermorePage from "./labs/livermore/LivermorePage"; // Added Livermore
import LivermoreHistoryPage from "./labs/livermore/LivermoreHistoryPage"; // Added Livermore History
import LivermoreUIPage from "./labs/livermore/LivermoreUIPage"; // Client Mode
import LivermoreV20Page from "./labs/livermoreV20/LivermoreV20Page"; // Livermore V2.0
import LivermoreV20HistoryPage from "./labs/livermoreV20/LivermoreV20HistoryPage"; // Livermore V2.0 History
import LivermoreV21Page from "./labs/livermoreV21/LivermoreV21Page"; // Livermore V2.1
import LivermoreV21HistoryPage from "./labs/livermoreV21/LivermoreV21HistoryPage"; // Livermore V2.1 History
import LivermoreV30Page from "./labs/livermoreV30/LivermoreV30Page"; // Livermore V3.0
import LivermoreV30HistoryPage from "./labs/livermoreV30/LivermoreV30HistoryPage"; // Livermore V3.0 History
import LivermoreV31Page from "./labs/livermoreV31/LivermoreV31Page"; // Livermore V3.1
import LivermoreV31HistoryPage from "./labs/livermoreV31/LivermoreV31HistoryPage"; // Livermore V3.1 History
import LivermoreV40Page from "./labs/livermoreV40/LivermoreV40Page"; // Livermore V4.0 (점수·랭킹)
import LivermoreV40HistoryPage from "./labs/livermoreV40/LivermoreV40HistoryPage"; // Livermore V4.0 History
import LivermoreV42Page from "./labs/livermoreV42/LivermoreV42Page"; // Livermore V4.2 (Dual Track: Flat Base + Momentum)
import LivermoreV42HistoryPage from "./labs/livermoreV42/LivermoreV42HistoryPage"; // Livermore V4.2 History
import WyckoffPage from "./labs/wyckoff/WyckoffPage";
import DataGeneratorPage from "./pages/DataGeneratorPage";
import TurtleDashboardPage from "./labs/turtle/TurtleDashboardPage"; // Turtle Trading
import TurtlePositionsPage from "./labs/turtle/TurtlePositionsPage"; // Turtle Positions
import TurtleJournalPage from "./labs/turtle/TurtleJournalPage";   // Turtle Journal
import TurtleSettingsPage from "./labs/turtle/TurtleSettingsPage";  // Turtle Settings
import TurtleScreenerPage from "./labs/turtle/TurtleScreenerPage";  // Turtle Screener
import TurtleMultiSimPage from "./labs/turtle/TurtleMultiSimPage"; // Turtle Multi Simulation
import MinerviniDashboardPage from "./labs/minervini/MinerviniDashboardPage"; // Minervini VCP
import MinerviniPositionsPage from "./labs/minervini/MinerviniPositionsPage";
import MinerviniJournalPage from "./labs/minervini/MinerviniJournalPage";
import MinerviniSettingsPage from "./labs/minervini/MinerviniSettingsPage";
import MinerviniScreenerPage from "./labs/minervini/MinerviniScreenerPage";
import VwapDashboardPage from "./labs/vwap/VwapDashboardPage";
import VwapPositionsPage from "./labs/vwap/VwapPositionsPage";
import VwapHistoryPage from "./labs/vwap/VwapHistoryPage";
import VwapScreenerPage from "./labs/vwapScreener/VwapScreenerPage";
import VwapScreenerHistoryPage from "./labs/vwapScreener/VwapScreenerHistoryPage";
import DtlScreenerPage from "./labs/dtlScreener/DtlScreenerPage";           // DTL-2Stage Screener
import DtlWatchlistPage from "./labs/dtlScreener/DtlWatchlistPage";         // DTL Watchlist
import DtlFocusListPage from "./labs/dtlScreener/DtlFocusListPage";         // DTL Focus List
import DtlPaperPage from "./labs/dtlScreener/DtlPaperPage";                 // DTL Paper Trading Dashboard
import IchingPage from "./labs/iching/IchingPage";                           // 주역×사주 AI
import RadarDashboardPage from "./labs/radar/RadarDashboardPage";           // 상한가 레이더
import RadarEventDetailPage from "./labs/radar/RadarEventDetailPage";       // 상한가 레이더 상세
import InstFlowDashboardPage from "./labs/instFlow/InstFlowDashboardPage";   // 기관수급분석
import { ToastProvider, useToast } from "./components/Toast"; // Import Toast

const AppContainer = styled.div`
  min-height: 100vh;
  background: #141414; /* Dark Mode Unified */
`;

const MainContent = styled.main`
  padding-top: 64px;
`;

function AppContent() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const { showToast } = useToast(); // Use Toast Hook

  useEffect(() => {
    // 로그인 상태 확인
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
  }, []);

  const handleLoginSuccess = (loggedInUser) => {
    setUser(loggedInUser);
    const name = (loggedInUser.displayName || loggedInUser.name || loggedInUser.email?.split('@')[0] || 'Member').trim();
    const displayName = name || 'Member'; // Double check in case trim results in empty
    showToast(`구글 로그인 성공! 환영합니다, ${displayName}님`, 'success'); // Toast!
    if (loggedInUser.isGuest) {
      navigate("/guest-guide");
    } else {
      navigate("/"); // 로그인 성공 후 Home으로 이동
    }
  };

  const handleLogout = () => {
    setUser(null);
    localStorage.removeItem("user");
    showToast("로그아웃 되었습니다.", 'info'); // Toast!
    navigate("/"); // Go to Landing Page
  };

  if (loading) {
    return <div>로딩 중...</div>;
  }

  // Guest Logic (Landing Page)
  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<LandingPage onLoginSuccess={handleLoginSuccess} />} />
        <Route path="/login" element={<LoginPage onLoginSuccess={handleLoginSuccess} />} />
        <Route path="/labs/iching" element={<IchingPage user={null} />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    );
  }

  // Authenticated Logic
  return (
    <AppContainer>
      <Header onLogout={handleLogout} user={user} />
      <MainContent>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/guest-guide" element={<GuestGuidePage />} />
          <Route path="/extract" element={<WordExtractor />} />
          <Route path="/sentence" element={<SentenceExtractor />} />
          <Route path="/word-history" element={<WordHistory />} />
          <Route path="/sentence-history" element={<SentenceHistory />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/quiz" element={<QuizPage />} />
          <Route path="/ai-study" element={<AIStudyPage />} />
          <Route path="/feed" element={<FeedPage />} />
          <Route path="/mystory" element={<MyStoryPage />} />
          <Route path="/community" element={<CommunityPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/batch-results" element={<BatchResultsPage />} />
          <Route path="/feed-settings" element={<FeedSettingsPage />} />
          <Route path="/deepl-test" element={<DeepLTestPage />} />

          {/* Labs Routes */}
          <Route path="/labs" element={<LabsPage />} />
          <Route path="/labs/stock" element={<StockPage />} />
          <Route path="/labs/stock/technical" element={<TechnicalPage />} />
          <Route path="/labs/backtest" element={<BacktestPage />} />
          <Route path="/labs/screener" element={<ScreenerPage />} />
          <Route path="/labs/screener/history" element={<ScreenerHistoryPage />} />
          <Route path="/labs/moneyflow" element={<MoneyFlowPage />} />
          <Route path="/labs/moneyflow/history" element={<MoneyFlowHistoryPage />} />
          <Route path="/labs/livermore" element={<LivermorePage />} />
          <Route path="/labs/livermoreUI" element={<LivermoreUIPage />} />
          <Route path="/labs/livermore/history" element={<LivermoreHistoryPage />} />
          <Route path="/labs/livermore-v20" element={<LivermoreV20Page />} />
          <Route path="/labs/livermore-v20/history" element={<LivermoreV20HistoryPage />} />
          <Route path="/labs/livermore-v21" element={<LivermoreV21Page />} />
          <Route path="/labs/livermore-v21/history" element={<LivermoreV21HistoryPage />} />
          <Route path="/labs/livermore-v30" element={<LivermoreV30Page />} />
          <Route path="/labs/livermore-v30/history" element={<LivermoreV30HistoryPage />} />
          <Route path="/labs/livermore-v31" element={<LivermoreV31Page />} />
          <Route path="/labs/livermore-v31/history" element={<LivermoreV31HistoryPage />} />
          <Route path="/labs/livermore-v40" element={<LivermoreV40Page />} />
          <Route path="/labs/livermore-v40/history" element={<LivermoreV40HistoryPage />} />
          <Route path="/labs/livermore-v42" element={<LivermoreV42Page />} />
          <Route path="/labs/livermore-v42/history" element={<LivermoreV42HistoryPage />} />
          <Route path="/labs/wyckoff" element={<WyckoffPage />} />
          <Route path="/data-generator" element={<DataGeneratorPage />} />
          {/* Turtle Trading Routes */}
          <Route path="/labs/turtle" element={<TurtleDashboardPage />} />
          <Route path="/labs/turtle/positions" element={<TurtlePositionsPage />} />
          <Route path="/labs/turtle/journal" element={<TurtleJournalPage />} />
          <Route path="/labs/turtle/settings" element={<TurtleSettingsPage />} />
          <Route path="/labs/turtle/screener" element={<TurtleScreenerPage />} />
          <Route path="/labs/turtle/multi-sim" element={<TurtleMultiSimPage />} />
          {/* Minervini VCP Trading Routes */}
          <Route path="/labs/minervini" element={<MinerviniDashboardPage />} />
          <Route path="/labs/minervini/positions" element={<MinerviniPositionsPage />} />
          <Route path="/labs/minervini/journal" element={<MinerviniJournalPage />} />
          <Route path="/labs/minervini/settings" element={<MinerviniSettingsPage />} />
          <Route path="/labs/minervini/screener" element={<MinerviniScreenerPage />} />
          {/* VWAP Mock Trading Routes */}
          <Route path="/labs/vwap" element={<VwapDashboardPage />} />
          <Route path="/labs/vwap/positions" element={<VwapPositionsPage />} />
          <Route path="/labs/vwap/history" element={<VwapHistoryPage />} />
          {/* VWAP Screener Routes */}
          <Route path="/labs/vwap-screener" element={<VwapScreenerPage />} />
          <Route path="/labs/vwap-screener/history" element={<VwapScreenerHistoryPage />} />
          {/* DTL-2Stage Screener Routes */}
          <Route path="/labs/dtl-screener" element={<DtlScreenerPage />} />
          <Route path="/labs/dtl-screener/watchlist" element={<DtlWatchlistPage />} />
          <Route path="/labs/dtl-screener/focus-list" element={<DtlFocusListPage />} />
          <Route path="/labs/dtl-paper" element={<DtlPaperPage />} />
          {/* 주역×사주 AI */}
          <Route path="/labs/iching" element={<IchingPage user={user} />} />
          {/* 상한가 레이더 */}
          <Route path="/labs/radar" element={<RadarDashboardPage />} />
          <Route path="/labs/radar/:eventId" element={<RadarEventDetailPage />} />
          <Route path="/labs/inst-flow" element={<InstFlowDashboardPage />} />

          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </MainContent>
    </AppContainer>
  );
}

function App() {
  const GOOGLE_CLIENT_ID =
    "650680215703-ueumm7v85og4dl17a86op0bkei3app4d.apps.googleusercontent.com";

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <ToastProvider>
        <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
          <AppContent />
        </Router>
      </ToastProvider>
    </GoogleOAuthProvider>
  );
}

export default App;